import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

const LS_KEY = 'nuyed_pwa_dismissed';
let deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event('nuyed:pwa-ready')); });
}

export function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function usePwaInstall() {
  const [ready, setReady] = useState(!!deferredPrompt);
  useEffect(() => {
    const h = () => setReady(true);
    window.addEventListener('nuyed:pwa-ready', h);
    return () => window.removeEventListener('nuyed:pwa-ready', h);
  }, []);
  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null; setReady(false);
    return outcome === 'accepted';
  };
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  return { ready, install, isIos, standalone: isStandalone() };
}

// Small, dismissable install hint (mobile only). Appears once after the first visit.
export default function PwaInstallBanner() {
  const { ready, install, isIos, standalone } = usePwaInstall();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (standalone) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(LS_KEY) === '1'; } catch {}
    if (dismissed) return;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    if (!mobile) return;
    const t = setTimeout(() => setShow(ready || isIos), 6000);
    return () => clearTimeout(t);
  }, [ready, isIos, standalone]);
  if (!show) return null;
  const dismiss = () => { setShow(false); try { localStorage.setItem(LS_KEY, '1'); } catch {} };
  return (
    <div className="gx-pwa-banner" role="dialog" data-testid="pwa-install-banner">
      <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold text-sm leading-tight">Install NUYED</p>
        <p className="text-[11px] text-gx-muted leading-snug">{isIos && !ready ? <>Tap <Share className="w-3 h-3 inline -mt-0.5" /> then “Add to Home Screen”.</> : 'Full-screen app with its own icon. No store needed.'}</p>
      </div>
      {ready && <button onClick={async () => { const ok = await install(); if (ok) dismiss(); }} className="gx-btn-primary text-xs px-3 py-2" data-testid="pwa-install-btn"><Download className="w-3.5 h-3.5" /> Install</button>}
      <button onClick={dismiss} className="p-1.5 rounded-lg text-gx-muted hover:text-white" aria-label="Dismiss" data-testid="pwa-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
